import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL;
const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const PERIODS = [1,2,3,4,5,6,7,8];
const COLORS = ['bg-blue-100 text-blue-800','bg-green-100 text-green-800','bg-purple-100 text-purple-800',
  'bg-yellow-100 text-yellow-800','bg-pink-100 text-pink-800','bg-orange-100 text-orange-800',
  'bg-teal-100 text-teal-800','bg-indigo-100 text-indigo-800'];

const subjectColor = (subject) => COLORS[Math.abs(subject.split('').reduce((a,c)=>a+c.charCodeAt(0),0))%COLORS.length];

const Timetable = () => {
  const { token, user } = useAuth();
  const [slots, setSlots]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm]         = useState({ day:'Monday', period:1, subject:'', teacher:'', room:'', start_time:'07:00', end_time:'07:45' });
  const [toast, setToast]       = useState(null);

  const showToast = (type,msg)=>{ setToast({type,msg}); setTimeout(()=>setToast(null),3500); };

  const fetchSlots = useCallback(async()=>{
    try {
      const res = await axios.get(`${API}/api/timetable`,{headers:{Authorization:`Bearer ${token}`}});
      setSlots(res.data);
    } catch(e){ console.error(e); }
    finally{ setLoading(false); }
  },[token]);

  useEffect(()=>{ fetchSlots(); },[fetchSlots]);

  const handleSave = async(e)=>{
    e.preventDefault(); if(!form.subject.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/timetable`,form,{headers:{Authorization:`Bearer ${token}`}});
      showToast('success','Slot saved!'); setShowForm(false);
      setForm({day:'Monday',period:1,subject:'',teacher:'',room:'',start_time:'07:00',end_time:'07:45'});
      fetchSlots();
    } catch(e){ showToast('error',e.response?.data?.message||'Error saving slot.'); }
    finally{ setSaving(false); }
  };

  const handleDelete = async(id)=>{
    if(!window.confirm('Delete this slot?')) return;
    setDeleting(id);
    try { await axios.delete(`${API}/api/timetable/${id}`,{headers:{Authorization:`Bearer ${token}`}}); fetchSlots(); }
    catch(e){ showToast('error','Could not delete.'); }
    finally{ setDeleting(null); }
  };

  // Build lookup: day → period → slot
  const lookup = {};
  slots.forEach(s=>{ if(!lookup[s.day]) lookup[s.day]={}; lookup[s.day][s.period]=s; });

  const today = new Date().toLocaleDateString('en-US',{weekday:'long'});

  return (
    <div className="min-h-screen bg-gray-100 py-4 sm:py-6 px-2 sm:px-4">
      {toast&&(
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-xl text-white text-sm font-medium max-w-[90vw] ${toast.type==='error'?'bg-red-500':'bg-whatsapp-green'}`}>
          <span>{toast.type==='error'?'⚠️':'✅'}</span><span>{toast.msg}</span>
          <button onClick={()=>setToast(null)} className="ml-1 opacity-70 hover:opacity-100">✕</button>
        </div>
      )}

      <div className="max-w-6xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-whatsapp-darker">📅 Class Timetable</h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5">S3 — Weekly Schedule</p>
          </div>
          {user?.role==='admin'&&(
            <button onClick={()=>setShowForm(p=>!p)}
              className="flex items-center gap-2 bg-whatsapp-green hover:bg-whatsapp-dark text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors shadow-md">
              <span>{showForm?'✕ Cancel':'+ Add Slot'}</span>
            </button>
          )}
        </div>

        {/* Add form */}
        {showForm&&user?.role==='admin'&&(
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5">
            <h2 className="text-base font-bold text-gray-800 mb-4">Add / Update Slot</h2>
            <form onSubmit={handleSave} className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                {label:'Day',      name:'day',        type:'select',  opts:DAYS },
                {label:'Period',   name:'period',     type:'select',  opts:PERIODS },
                {label:'Subject',  name:'subject',    type:'text',    placeholder:'e.g. Mathematics'},
                {label:'Teacher',  name:'teacher',    type:'text',    placeholder:'e.g. Mr. Niyigena'},
                {label:'Room',     name:'room',       type:'text',    placeholder:'e.g. Room 12'},
                {label:'Start',    name:'start_time', type:'time'    },
                {label:'End',      name:'end_time',   type:'time'    },
              ].map(f=>(
                <div key={f.name}>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">{f.label}</label>
                  {f.type==='select'
                    ? <select value={form[f.name]} onChange={e=>setForm(p=>({...p,[f.name]:f.name==='period'?parseInt(e.target.value):e.target.value}))}
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-whatsapp-green">
                        {f.opts.map(o=><option key={o} value={o}>{o}</option>)}
                      </select>
                    : <input type={f.type} value={form[f.name]} onChange={e=>setForm(p=>({...p,[f.name]:e.target.value}))}
                        placeholder={f.placeholder||''} required={f.name==='subject'}
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-whatsapp-green"/>
                  }
                </div>
              ))}
              <div className="col-span-2 sm:col-span-3">
                <button type="submit" disabled={saving||!form.subject.trim()}
                  className="w-full bg-whatsapp-green hover:bg-whatsapp-dark text-white py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-60">
                  {saving?'Saving…':'Save Slot'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Mobile: cards per day */}
        <div className="block lg:hidden space-y-3">
          {loading ? <div className="text-center py-10 text-gray-400">Loading…</div>
          : DAYS.map(day=>{
            const isToday = day===today;
            const daySlots = slots.filter(s=>s.day===day);
            if(daySlots.length===0) return null;
            return (
              <div key={day} className={`bg-white rounded-2xl shadow-sm border overflow-hidden ${isToday?'border-whatsapp-green':'border-gray-100'}`}>
                <div className={`px-4 py-2.5 flex items-center justify-between ${isToday?'bg-whatsapp-green':'bg-gray-50 border-b border-gray-100'}`}>
                  <h3 className={`font-bold text-sm ${isToday?'text-white':'text-gray-700'}`}>{day}</h3>
                  {isToday&&<span className="text-white text-xs bg-white/20 px-2 py-0.5 rounded-full">Today</span>}
                </div>
                <div className="divide-y divide-gray-50">
                  {daySlots.sort((a,b)=>a.period-b.period).map(s=>(
                    <div key={s.id} className="px-4 py-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-xs font-black text-gray-400 w-4 flex-shrink-0">{s.period}</span>
                        <div className="min-w-0">
                          <span className={`inline-block px-2 py-0.5 rounded-lg text-xs font-bold ${subjectColor(s.subject)}`}>{s.subject}</span>
                          {s.teacher&&<p className="text-xs text-gray-500 mt-0.5 truncate">{s.teacher}{s.room?` · ${s.room}`:''}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-gray-400 tabular-nums">{s.start_time?.slice(0,5)}–{s.end_time?.slice(0,5)}</span>
                        {user?.role==='admin'&&(
                          <button onClick={()=>handleDelete(s.id)} disabled={deleting===s.id}
                            className="text-gray-300 hover:text-red-500 transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop: grid table */}
        <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 w-20">Period</th>
                  {DAYS.map(d=>(
                    <th key={d} className={`px-3 py-3 text-left text-xs font-bold ${d===today?'text-whatsapp-green':'text-gray-500'}`}>
                      {d}{d===today&&<span className="ml-1 text-xxs bg-whatsapp-green text-white px-1.5 py-0.5 rounded-full">Today</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-10 text-gray-400">Loading timetable…</td></tr>
                ) : PERIODS.map(p=>(
                  <tr key={p} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 font-black text-gray-400 text-xs">{p}</td>
                    {DAYS.map(d=>{
                      const s = lookup[d]?.[p];
                      return (
                        <td key={d} className={`px-3 py-3 ${d===today?'bg-green-50/50':''}`}>
                          {s ? (
                            <div className="group relative">
                              <span className={`inline-block px-2 py-1 rounded-lg text-xs font-bold ${subjectColor(s.subject)}`}>{s.subject}</span>
                              {s.teacher&&<p className="text-xs text-gray-400 mt-0.5 truncate">{s.teacher}</p>}
                              {s.room&&<p className="text-xs text-gray-300">{s.room}</p>}
                              <p className="text-xs text-gray-300 tabular-nums">{s.start_time?.slice(0,5)}</p>
                              {user?.role==='admin'&&(
                                <button onClick={()=>handleDelete(s.id)} disabled={deleting===s.id}
                                  className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all p-0.5">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                                </button>
                              )}
                            </div>
                          ) : <span className="text-gray-200 text-xs">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Timetable;
